import { Link } from '@tanstack/react-router';
import { postMeta } from './manifest';
import type { Post } from './types';
import { H2, OL, P, Pull, UL } from './ui';

const post: Post = { meta: postMeta('what-is-a-beat-battle'), Body };
export default post;

function Body() {
  return (
    <>
      <P>
        A beat battle is a competition between music producers. Each producer makes an original
        instrumental under a shared constraint, a judge panel or live audience scores the entries
        blind, and the highest score wins. That is the definition. The rest of this guide covers
        where beat battles came from, how they work today, and why they became the fastest way for
        producers to get real feedback on their work.
      </P>

      <H2 id="history">Where beat battles came from</H2>
      <P>
        Beat battles have roots in two places: the sample-flip cyphers of 1990s hip-hop and the
        early internet music forums of the late 1990s.
      </P>
      <P>
        The cypher tradition goes back further. Producers in New York and Detroit would sit in
        studios with the same drum machine, sample each other's records, and see who could flip the
        same source material into something better. J Dilla was famously involved in informal
        sessions like this in Detroit in the mid-1990s. The idea that skill, not equipment,
        separates producers is baked into that era.
      </P>
      <P>
        The online version started around 1999 to 2001 on forums like{' '}
        <a
          href="https://www.illmuzik.com"
          rel="noreferrer nofollow noopener"
          target="_blank"
          className="underline decoration-primary/50 underline-offset-2"
        >
          IllMuzik
        </a>
        . Members would post a sample or a theme, producers would have a week to make a beat from
        it, and then everyone in the thread would vote. The format was rough - MP3s in forum
        attachments, subjective scoring - but it worked. IllMuzik's Beat Battle League is still
        running today and has been going continuously since around 2002, making it one of the
        longest uninterrupted online producer competitions on the internet.
      </P>
      <P>
        The 2010s saw beat battles move to SoundCloud comment threads, Reddit's r/makinghiphop
        monthly challenges, and early Discord servers. By 2020, dedicated platforms started building
        real-time versions: a timer, a shared sample kit, automatic submission, and blind voting all
        inside a single browser window. That is the modern format most people mean when they say
        "beat battle" today.
      </P>

      <H2 id="how-it-works">How a modern beat battle works</H2>
      <P>
        The structure is almost universal across platforms, with variation in timing and kit rules:
      </P>
      <OL>
        <li>
          <strong>Lobby.</strong> Producers join the match. The platform locks in genre, BPM range,
          and whether outside samples are allowed. On Prod Battle, the matchmaker does this
          automatically based on the genre you pick.
        </li>
        <li>
          <strong>Kit drop.</strong> At match start, a sample kit drops to every producer at the
          same moment. Same drums. Same melodies. Same one-shots. The kit is the constraint.
        </li>
        <li>
          <strong>Build phase.</strong> Producers work in their own DAW and have 15 to 30 minutes to
          make a beat. They can use any technique - they just have to start from the shared kit.
        </li>
        <li>
          <strong>Submit.</strong> Each producer exports their beat and uploads it before the timer
          ends. No extensions.
        </li>
        <li>
          <strong>Vote.</strong> The audience listens to every entry with producer names hidden.
          They score on a 1-to-5 scale. Highest average score wins.
        </li>
        <li>
          <strong>Results.</strong> Scores post publicly. Rankings update if the match was ranked.
          Every submitted beat goes onto the producer's public profile.
        </li>
      </OL>

      <H2 id="formats">Beat battle formats</H2>
      <P>Not all beat battles run on the same timer or the same rules. The main variations:</P>
      <UL>
        <li>
          <strong>Real-time, shared kit.</strong> Everyone makes a beat live. Same kit, same timer.
          The most skill-intensive format. This is what{' '}
          <Link to="/play" className="underline decoration-primary/50 underline-offset-2">
            Prod Battle
          </Link>{' '}
          runs.
        </li>
        <li>
          <strong>Async with a theme.</strong> A theme is posted (a sample clip, a BPM, a mood).
          Producers have 24 hours to a week to submit. Voting happens at the end. IllMuzik uses this
          format.
        </li>
        <li>
          <strong>Tournament bracket.</strong> Many producers enter. Win your match to advance, lose
          and you are eliminated. Weekly and monthly tournament brackets on most platforms work this
          way.
        </li>
        <li>
          <strong>Sample flip.</strong> Everyone gets the same source audio - a few bars of a song,
          a found recording, a vocal snippet - and has to produce something new from it. The
          creative floor is higher because the source material is the same for everyone.
        </li>
        <li>
          <strong>Speed battle.</strong> 5 to 10 minutes total. No polish, no overthinking. Tests
          raw workflow speed.
        </li>
        <li>
          <strong>FFA vs 1v1.</strong> Free-for-all puts everyone in the same match and they all
          compete against each other. 1v1 is head-to-head. FFA is better for rankings; 1v1 is better
          for targeted grudge matches or tournaments.
        </li>
      </UL>

      <H2 id="judging">How judging works</H2>
      <P>
        Blind voting is the standard for almost every serious platform. The logic is simple: if
        voters can see who made a beat, they vote for the producer they already like, not the beat
        that actually hit harder. Blind voting forces the beat to carry the vote.
      </P>
      <P>
        Most platforms use a 1-to-5 numeric scale. Some add weighted voting where accounts with
        longer history or higher reputation carry more weight per vote. This reduces the impact of
        accounts that sign up just to vote-trade with friends.
      </P>
      <P>
        Some battles add a judge panel - two or three established producers who score entries on
        multiple criteria (drums, melody, arrangement, originality). Panel scoring takes longer but
        tends to reward producers with more sophisticated technique. Audience scoring is faster and
        rewards what the crowd actually responds to, which is usually hooks and loudness over
        technicality.
      </P>
      <P>
        On Prod Battle, audience voting is the default with{' '}
        <a href="/faq#voting" className="underline decoration-primary/50 underline-offset-2">
          anti-manipulation checks
        </a>{' '}
        built in. You can read the exact voting rules on the FAQ.
      </P>

      <H2 id="ranking">What happens to your score</H2>
      <P>In casual or quickplay matches, a win is just a win. No ranking points move.</P>
      <P>
        In ranked play, wins and losses adjust your genre rating using an ELO-style algorithm. Beat
        a higher-rated producer and you earn more points. Lose to a lower-rated one and you lose
        more. After roughly 10 ranked matches your rating stabilizes and you get a tier: bronze,
        silver, gold, platinum, diamond, or masters.
      </P>
      <P>
        The{' '}
        <Link to="/leaderboard" className="underline decoration-primary/50 underline-offset-2">
          leaderboard
        </Link>{' '}
        is per genre. A trap producer's ranking does not affect their lo-fi ranking. This means you
        can grind trap matches without touching your position in other genres.
      </P>

      <H2 id="why-producers-do-it">Why producers compete in beat battles</H2>
      <P>
        The most honest answer: beat battles give you feedback you cannot get from SoundCloud plays.
      </P>
      <P>
        When you post a beat online, you get silence or vague praise. When you enter a beat battle,
        you get a score against real-time competition. You know immediately whether your beat won or
        lost, you can hear what beat it lost to, and you can figure out why. That feedback loop -
        make, submit, compare, adjust - is what accelerates producers faster than any other practice
        method.
      </P>
      <P>
        There is also the constraint effect. Having 20 minutes and a shared kit forces decisions.
        You cannot overthink it. You cannot keep opening a new project because the old one is not
        perfect yet. The constraint is the teacher.
      </P>

      <Pull>
        The fastest way to know if your beats are actually good is to put them up against other
        people's beats on a clock.
      </Pull>

      <H2 id="beat-battle-vs-producer-battle">Beat battle vs producer battle</H2>
      <P>
        The terms are used interchangeably. "Beat battle" is older and more widespread - it comes
        from the hip-hop production community. "Producer battle" is a newer framing that some
        platforms use to signal that the competition covers more genres than just hip-hop, including
        house, drum and bass, techno, and pop production.
      </P>
      <P>
        On this platform, both terms refer to the same thing. The{' '}
        <Link to="/genres" className="underline decoration-primary/50 underline-offset-2">
          genres page
        </Link>{' '}
        shows every active genre. Hip-hop and trap producers will feel at home, but so will house,
        DnB, and lo-fi producers.
      </P>

      <H2 id="getting-started">How to enter your first beat battle today</H2>
      <OL>
        <li>
          Sign up free at{' '}
          <Link to="/auth/sign-up" className="underline decoration-primary/50 underline-offset-2">
            Prod Battle
          </Link>
          . Takes about 30 seconds with an email, Google, or Discord account.
        </li>
        <li>
          Pick a genre from the{' '}
          <Link to="/genres" className="underline decoration-primary/50 underline-offset-2">
            genres list
          </Link>
          . Pick the one you make most regularly, not the one you think sounds hardest.
        </li>
        <li>
          Hit{' '}
          <Link to="/play" className="underline decoration-primary/50 underline-offset-2">
            Play
          </Link>{' '}
          and choose Quickplay. You will be matched into the next available battle automatically.
          Quickplay does not affect your ranked rating.
        </li>
        <li>
          When the kit drops, download it and start your DAW. Make something. Export. Upload it
          before the timer ends.
        </li>
        <li>
          After voting, check the results. Listen to every other entry. Note what beat you lost to
          and what it did differently.
        </li>
      </OL>
      <P>
        Read{' '}
        <Link
          to="/blog/$slug"
          params={{ slug: 'how-to-win-a-beat-battle' }}
          className="underline decoration-primary/50 underline-offset-2"
        >
          how to win a beat battle
        </Link>{' '}
        before your first ranked match. The tactics in that post are specific to the blind-vote
        format and will save you a few losses while you calibrate.
      </P>

      <H2 id="bottom-line">Bottom line</H2>
      <P>
        A beat battle is a timed, constrained, scored production competition. The constraint (shared
        kit, shared timer) removes gear as a variable. The blind voting removes popularity as a
        variable. What remains is whether your beat hit harder than the others in this room on this
        day.
      </P>
      <P>
        That is a harder, faster, and more honest feedback loop than anything else available to
        working producers. That is why they exist.
      </P>
    </>
  );
}
